/*
 * Creative Analysis reports rail (2026-10-28, plan 2026-10-28-001 PR 3).
 *
 * Built-in reports (the Raya set: angles by segment, awareness, format,
 * team) plus the brand's saved reports for this product. Opening a report
 * rewrites the view's URL params; saving stores them under a name. Anyone in
 * the brand can save and open reports; only the creator or a super-admin can
 * rename or delete one (the server enforces it, `can_edit` drives the UI).
 */
import { useState } from "react";
import { ActionIcon, Collapse, Group, Paper, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";

import {
  useCreateSavedReport,
  useDeleteSavedReport,
  useSavedReports,
  useUpdateSavedReport,
} from "../api/queries";
import { ApiError } from "../api/client";
import { Button } from "../components/ui";
import { BUILT_IN_REPORTS, cleanState, sameView, type ReportState } from "./report-state";

export interface ReportsRailProps {
  /** The product saved reports belong to; null on the all-products view. */
  productId: string | null;
  current: ReportState;
  onOpen: (state: ReportState) => void;
  /** Absolute link that opens `state` on this product. */
  linkFor: (state: ReportState) => string;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError && e.status === 409) return "A report with that name already exists.";
  if (e instanceof ApiError && e.status === 403) return "Only the person who saved it, or a super-admin, can change it.";
  return "Couldn't save the report. Try again.";
}

function ReportButton({ name, active, onClick }: { name: string; active: boolean; onClick: () => void }) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      style={{
        display: "block",
        width: "100%",
        padding: "4px 8px",
        borderRadius: 6,
        fontSize: "var(--text-sm)",
        background: active ? "var(--color-accent-subtle)" : undefined,
        color: active ? "var(--color-accent)" : "var(--color-text)",
        fontWeight: active ? 600 : 400,
        textAlign: "left",
      }}
    >
      {name}
    </UnstyledButton>
  );
}

export function ReportsRail({ productId, current, onOpen, linkFor }: ReportsRailProps) {
  const wide = useMediaQuery("(min-width: 62em)");
  const [openOnPhone, setOpenOnPhone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const saved = useSavedReports(productId ?? "");
  const create = useCreateSavedReport();
  const update = useUpdateSavedReport();
  const remove = useDeleteSavedReport();

  const copyLink = async (state: ReportState) => {
    try {
      await navigator.clipboard.writeText(linkFor(state));
      notifications.show({ message: "Link copied" });
    } catch {
      notifications.show({ color: "red", message: "Couldn't copy the link" });
    }
  };

  const save = () => {
    if (!productId || !newName.trim()) return;
    create.mutate(
      { product_id: productId, name: newName.trim(), state: current },
      {
        onSuccess: () => {
          setSaving(false);
          setNewName("");
          notifications.show({ message: "Report saved" });
        },
        onError: (e) => notifications.show({ color: "red", message: errorText(e) }),
      },
    );
  };

  const body = (
    <Stack gap={4}>
      <Text fz="xs" fw={600} c="dimmed" tt="uppercase" mt={4}>
        Built-in
      </Text>
      {BUILT_IN_REPORTS.map((r) => (
        <ReportButton key={r.id} name={r.name} active={sameView(current, r.state)} onClick={() => onOpen(r.state)} />
      ))}

      <Text fz="xs" fw={600} c="dimmed" tt="uppercase" mt="sm">
        Saved
      </Text>
      {productId === null ? (
        <Text fz="xs" c="dimmed">
          Saved reports belong to a product. Pick one to see them.
        </Text>
      ) : saved.isError ? (
        <Text fz="xs" c="dimmed">
          Couldn&apos;t load saved reports. Refresh to try again.
        </Text>
      ) : saved.isPending ? (
        <Text fz="xs" c="dimmed">
          Loading…
        </Text>
      ) : (saved.data?.reports ?? []).length === 0 ? (
        <Text fz="xs" c="dimmed">
          None yet. Set up a view and save it.
        </Text>
      ) : (
        (saved.data?.reports ?? []).map((r) => {
          const state = cleanState(r.state);
          if (renaming === r.id) {
            return (
              <Group key={r.id} gap={4} wrap="nowrap">
                <TextInput
                  size="xs"
                  value={renameTo}
                  onChange={(e) => setRenameTo(e.currentTarget.value)}
                  aria-label="New name"
                  style={{ flex: 1 }}
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={() =>
                    update.mutate(
                      { id: r.id, name: renameTo },
                      {
                        onSuccess: () => setRenaming(null),
                        onError: (e) => notifications.show({ color: "red", message: errorText(e) }),
                      },
                    )
                  }
                >
                  Save
                </Button>
              </Group>
            );
          }
          return (
            <Group key={r.id} gap={2} wrap="nowrap" justify="space-between">
              <div style={{ flex: 1, minWidth: 0 }}>
                <ReportButton name={r.name} active={sameView(current, state)} onClick={() => onOpen(state)} />
              </div>
              <ActionIcon variant="subtle" size="sm" aria-label={`Copy link to ${r.name}`} onClick={() => void copyLink(state)}>
                ⧉
              </ActionIcon>
              {r.can_edit && (
                <>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    aria-label={`Rename ${r.name}`}
                    onClick={() => {
                      setRenaming(r.id);
                      setRenameTo(r.name);
                    }}
                  >
                    ✎
                  </ActionIcon>
                  {confirmDelete === r.id ? (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        remove.mutate(
                          { id: r.id, productId: productId! },
                          { onSettled: () => setConfirmDelete(null) },
                        )
                      }
                    >
                      Delete?
                    </Button>
                  ) : (
                    <ActionIcon variant="subtle" size="sm" color="red" aria-label={`Delete ${r.name}`} onClick={() => setConfirmDelete(r.id)}>
                      ✕
                    </ActionIcon>
                  )}
                </>
              )}
            </Group>
          );
        })
      )}

      {productId !== null &&
        (saving ? (
          <Stack gap={4} mt="xs">
            <TextInput
              size="xs"
              placeholder="Report name"
              value={newName}
              onChange={(e) => setNewName(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
              }}
              aria-label="Report name"
              maxLength={80}
              autoFocus
            />
            <Group gap={4}>
              <Button size="sm" onClick={save} disabled={!newName.trim() || create.isPending}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSaving(false)}>
                Cancel
              </Button>
            </Group>
          </Stack>
        ) : (
          <Group gap={4} mt="xs">
            <Button size="sm" variant="secondary" onClick={() => setSaving(true)}>
              Save this view
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void copyLink(current)}>
              Copy link
            </Button>
          </Group>
        ))}
    </Stack>
  );

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between">
        <Text fw={600} fz="sm">
          Reports
        </Text>
        {!wide && (
          <UnstyledButton onClick={() => setOpenOnPhone((o) => !o)} aria-expanded={openOnPhone} fz="xs" c="dimmed">
            {openOnPhone ? "Hide" : "Show"}
          </UnstyledButton>
        )}
      </Group>
      {wide ? body : <Collapse expanded={openOnPhone}>{body}</Collapse>}
    </Paper>
  );
}
